/**
 * Pure derivation functions for the Recommendation Engine coach views.
 * Live-computed numbers (rollups, counts, plan estimate) — no React, fully testable.
 * FSRS-specific cascade numbers are NOT here; they are scripted in `_mock-data/cascades.ts`.
 */

import {
  classifyMasteryBucket,
  hasCustomRollupWeight,
  weightedAverage,
} from '@/lib/recommendation/proficiency-range'
import { rollUpSubtopicMastery } from '@/lib/recommendation/topic-mastery-rollup'
import type { SubtopicState, TopicState } from '../types'

/** Tunable effort constant: hours of practice per point of mastery gap. */
export const HOURS_PER_GAP_POINT = 0.5

export type TopicBucket = 'remaining' | 'learning' | 'proficient' | 'mastered'

export interface FsrsEligibilityResult {
  eligible: boolean
  reasons: string[]
}

export interface FsrsEligibilityInput {
  selfScore: 1 | 2 | 3 | 4 | null
  reportedApproach: { type: string } | null
}

export interface AttemptIngestionInput extends FsrsEligibilityInput {
  knowledgeMapSeeded: boolean
  attemptSubmittedAt: Date | string | null
  baselineEpochAt: Date | null
}

function parseAttemptTimestamp(value: Date | string | null): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Postmortem completeness only — self-grade and reported approach present.
 */
export function isFsrsEligibleAttempt(
  input: FsrsEligibilityInput,
): FsrsEligibilityResult {
  const reasons: string[] = []
  if (input.selfScore === null) {
    reasons.push('Missing self-grade')
  }
  if (!input.reportedApproach || input.reportedApproach.type.trim() === '') {
    reasons.push('Missing reported approach')
  }
  return { eligible: reasons.length === 0, reasons }
}

/**
 * Full recommendation-engine ingestion gate (V1):
 * seeded knowledge map + in-epoch attempt + complete postmortem.
 * Calibration study-plan membership is not considered.
 */
export function isAttemptIngestibleForEngine(
  input: AttemptIngestionInput,
): FsrsEligibilityResult {
  const reasons: string[] = []

  if (!input.knowledgeMapSeeded) {
    reasons.push('Knowledge map not seeded yet')
  }

  const attemptAt = parseAttemptTimestamp(input.attemptSubmittedAt)
  const epochAt = input.baselineEpochAt
  if (!epochAt) {
    reasons.push('Baseline epoch not set')
  } else if (!attemptAt) {
    reasons.push('Missing submission timestamp')
  } else if (attemptAt < epochAt) {
    reasons.push('Before baseline epoch')
  }

  const postmortem = isFsrsEligibleAttempt(input)
  if (!postmortem.eligible) {
    reasons.push(...postmortem.reasons)
  }

  return { eligible: reasons.length === 0, reasons }
}

export function ingestionInputFromCompletedWork(
  work: FsrsEligibilityInput & { completedAt: string },
  context: Pick<
    AttemptIngestionInput,
    'knowledgeMapSeeded' | 'baselineEpochAt'
  >,
): AttemptIngestionInput {
  return {
    ...context,
    selfScore: work.selfScore,
    reportedApproach: work.reportedApproach,
    attemptSubmittedAt: work.completedAt,
  }
}

/** Per-subtopic desired retention R_d from coach goal (DEV-4 extension). */
export function getSubtopicRd(
  subtopic: Pick<SubtopicState, 'maxProficiencyThreshold'>,
  topic: Pick<TopicState, 'maxProficiencyThreshold'>,
): number {
  return (
    (subtopic.maxProficiencyThreshold ?? topic.maxProficiencyThreshold) / 100
  )
}

/**
 * Whether a subtopic participates in rollups, ranking, and FSRS replay.
 * Topic plan inclusion is the master switch.
 */
export function isSubtopicActive(
  subtopic: Pick<SubtopicState, 'isEnabled'>,
  topic: Pick<TopicState, 'includedInPlan'>,
): boolean {
  return topic.includedInPlan && subtopic.isEnabled !== false
}

/** Active subtopics whose rollup weight differs from the default (1). */
export function countCustomWeightedSubtopics(
  subtopics: SubtopicState[],
  topic: Pick<TopicState, 'includedInPlan'>,
): number {
  return subtopics.filter(
    (st) =>
      isSubtopicActive(st, topic) && hasCustomRollupWeight(st.rollupWeight),
  ).length
}

/** Rollup of a topic's subtopic effective mastery weighted by `rollupWeight`. */
export function rollUpTopicMastery(
  subtopics: SubtopicState[],
  topic?: Pick<TopicState, 'includedInPlan'>,
): number {
  if (topic && !topic.includedInPlan) return 0
  const active = topic
    ? subtopics.filter((st) => isSubtopicActive(st, topic))
    : subtopics
  // The weighting itself lives in `lib/recommendation` so the client
  // dashboard's mastery map can share it — `api/` cannot import from
  // `components/`, and a second copy there would drift from this one.
  return rollUpSubtopicMastery(active)
}

export function rollupTopicProficiencyBounds(
  subtopics: SubtopicState[],
  topic: Pick<
    TopicState,
    'includedInPlan' | 'minProficiencyThreshold' | 'maxProficiencyThreshold'
  >,
): {
  minProficiencyThreshold: number
  maxProficiencyThreshold: number
} {
  if (!topic.includedInPlan) {
    return {
      minProficiencyThreshold: topic.minProficiencyThreshold,
      maxProficiencyThreshold: topic.maxProficiencyThreshold,
    }
  }
  const active = subtopics.filter((st) => isSubtopicActive(st, topic))
  if (active.length === 0) {
    return {
      minProficiencyThreshold: topic.minProficiencyThreshold,
      maxProficiencyThreshold: topic.maxProficiencyThreshold,
    }
  }
  return {
    minProficiencyThreshold: weightedAverage(
      active.map((st) => ({
        value: st.minProficiencyThreshold ?? topic.minProficiencyThreshold,
        weight: st.rollupWeight ?? 1,
      })),
    ),
    maxProficiencyThreshold: weightedAverage(
      active.map((st) => ({
        value: st.maxProficiencyThreshold ?? topic.maxProficiencyThreshold,
        weight: st.rollupWeight ?? 1,
      })),
    ),
  }
}

/**
 * Same rollup as `rollUpTopicMastery`, but from live inspector row values
 * (`effectiveMasteryLive` + `isEnabled`) so topic headers match per-row display.
 */
export function rollUpTopicMasteryFromLive(
  rows: { effectiveMasteryLive: number; isEnabled: boolean }[],
  topicIncludedInPlan = true,
): number {
  if (!topicIncludedInPlan) return 0
  return weightedAverage(
    rows
      .filter((r) => r.isEnabled)
      .map((r) => ({ value: r.effectiveMasteryLive, weight: 1 })),
  )
}

/** True when at least one active subtopic has been attempted. */
function isStarted(topic: TopicState): boolean {
  return topic.subtopics.some(
    (st) => isSubtopicActive(st, topic) && st.lastAttemptAt !== null,
  )
}

/**
 * Classifies a topic into exactly one bucket, in precedence order:
 * gated or not-started → remaining; at/above goal → mastered; otherwise learning.
 */
export function classifyTopicBucket(topic: TopicState): TopicBucket {
  if (!topic.prerequisitesMet) return 'remaining'
  if (!topic.includedInPlan) return 'remaining'
  if (!isStarted(topic)) return 'remaining'
  return classifyMasteryBucket(
    rollUpTopicMastery(topic.subtopics, topic),
    topic.minProficiencyThreshold,
    topic.maxProficiencyThreshold,
  )
}

export function computeBucketCounts(topics: TopicState[]): {
  mastered: number
  proficient: number
  learning: number
  remaining: number
} {
  const counts = { mastered: 0, proficient: 0, learning: 0, remaining: 0 }
  for (const topic of topics) {
    counts[classifyTopicBucket(topic)] += 1
  }
  return counts
}

/** Mean topic rollup across included topics — the headline "Total Progress" number. */
export function computeOverallMastery(topics: TopicState[]): number {
  const included = topics.filter((t) => t.includedInPlan)
  if (included.length === 0) return 0
  const sum = included.reduce(
    (acc, t) => acc + rollUpTopicMastery(t.subtopics, t),
    0,
  )
  return Math.round(sum / included.length)
}

export interface PlanDurationEstimate {
  totalGapPoints: number
  totalHours: number
  weeks: number
  days: number
}

/**
 * Estimates plan length from the summed mastery gap of *included* topics and the
 * weekly commitment. Placeholder heuristic — tunable, not load-bearing.
 * Returns null when it cannot be computed (no weekly hours, or no included topics).
 */
export function estimatePlanDuration(
  topics: TopicState[],
  weeklyCommitmentHours: number,
): PlanDurationEstimate | null {
  if (weeklyCommitmentHours <= 0) return null
  const included = topics.filter((t) => t.includedInPlan)
  if (included.length === 0) return null
  let totalGapPoints = 0
  for (const topic of included) {
    const gap =
      topic.maxProficiencyThreshold - rollUpTopicMastery(topic.subtopics, topic)
    totalGapPoints += Math.max(0, gap)
  }
  const totalHours = totalGapPoints * HOURS_PER_GAP_POINT
  const weeks = totalHours / weeklyCommitmentHours
  const days = Math.ceil(weeks * 7)
  return { totalGapPoints, totalHours, weeks, days }
}

/** Start date + estimated days. `from` defaults to now. Uses UTC arithmetic to avoid DST drift. */
export function projectedFinishDate(estimatedDays: number, from?: Date): Date {
  const start = from ?? new Date()
  const finish = new Date(start)
  finish.setUTCDate(finish.getUTCDate() + estimatedDays)
  return finish
}

export type FitsStatus = 'fits' | 'over' | 'overdue' | 'none'

export interface FitsIndicator {
  status: FitsStatus
  /** Days of buffer ('fits') or days over ('over'/'overdue'); 0 for 'none'. */
  deltaDays: number
}

const MS_PER_DAY = 1000 * 60 * 60 * 24

/**
 * Compares the projected finish against an optional target date.
 * All comparisons are day-exact: dates are floored to UTC day-indices so
 * time-of-day and DST cannot shift the overdue/fits/over boundary.
 * A target equal to today is NOT overdue — it falls through to fits/over.
 */
export function computeFitsIndicator(
  projectedFinish: Date,
  targetDate: string | null,
  now?: Date,
): FitsIndicator {
  if (!targetDate) return { status: 'none', deltaDays: 0 }
  /** Floor a Date to its integer UTC day-index so comparisons are day-exact. */
  const dayIndex = (d: Date) => Math.floor(d.getTime() / MS_PER_DAY)
  const targetDay = dayIndex(new Date(`${targetDate}T00:00:00Z`))
  const todayDay = dayIndex(now ?? new Date())
  const finishDay = dayIndex(projectedFinish)
  if (targetDay < todayDay) {
    return { status: 'overdue', deltaDays: todayDay - targetDay }
  }
  const deltaDays = targetDay - finishDay
  if (deltaDays >= 0) return { status: 'fits', deltaDays }
  return { status: 'over', deltaDays: Math.abs(deltaDays) }
}
